// Bounded session resolution.
//
// A tab left open for hours can leave the Supabase auth client in a state where
// getSession() rejects (expired / invalid refresh token) or never settles (the
// background refresh is stuck holding the internal lock). Any screen that awaits
// it before rendering then hangs on "Loading…" forever. These helpers make the
// wait bounded and give callers a definite answer.
import type { Session, SupabaseClient } from "@supabase/supabase-js";

export const SESSION_TIMEOUT_MS = 8000;

export type SessionOutcome = {
  session: Session | null;
  /** true when the session could not be resolved (error or timeout). */
  failed: boolean;
  reason?: "error" | "timeout";
};

/**
 * Resolve the current session, never hanging longer than `timeoutMs`.
 * On failure the caller should treat the visitor as signed out.
 */
export async function getSessionSafe(
  client: Pick<SupabaseClient, "auth">,
  timeoutMs = SESSION_TIMEOUT_MS,
): Promise<SessionOutcome> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<SessionOutcome>((resolve) => {
      timer = setTimeout(() => resolve({ session: null, failed: true, reason: "timeout" }), timeoutMs);
    });
    const lookup = client.auth
      .getSession()
      .then(({ data, error }): SessionOutcome =>
        error
          ? { session: null, failed: true, reason: "error" }
          : { session: data.session ?? null, failed: false },
      )
      .catch((): SessionOutcome => ({ session: null, failed: true, reason: "error" }));
    return await Promise.race([lookup, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Drop the persisted auth token so the next page load starts clean instead of
 * retrying a refresh token the server has already rejected.
 */
export function clearStaleAuthStorage() {
  if (typeof window === "undefined") return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith("sb-") && key.endsWith("-auth-token")) keys.push(key);
    }
    keys.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // storage unavailable (private mode / quota) — nothing to clean up
  }
}

/** Same-origin path (with query) to return to after signing back in. */
export function currentReturnPath(): string {
  if (typeof window === "undefined") return "/";
  return window.location.pathname + window.location.search;
}

/** Only accept same-origin absolute paths as a post-login destination. */
export function sanitizeReturnPath(value: string | null | undefined, fallback: string): string {
  if (!value) return fallback;
  if (!value.startsWith("/") || value.startsWith("//")) return fallback;
  return value;
}
